/**
 * ManuscriptReviewReportApplication
 * Application service for the Manuscript Review Report
 * Extends BaseReportApplication with typed accessors for manuscript review data
 */

import { BaseReportApplication } from '../../../services/baseReport/BaseReportApplication';
import { ReportConfig } from '../../../services/baseReport/types';
import {
  ManuscriptReviewReportData,
  ManuscriptAggregateMetrics,
  ParagraphReview,
  MANUSCRIPT_REVIEW_REPORT_CONFIG,
} from './ManuscriptReviewReportConfig';

// Paragraph score fields that map 1-to-1 to aggregate dimension keys
type ParagraphScoreKey = 'pacing' | 'engagement' | 'clarity' | 'prose_quality' | 'dialogue_quality' | 'emotional_impact';

export class ManuscriptReviewReportApplication extends BaseReportApplication<ManuscriptReviewReportData> {
  constructor() {
    super();
  }

  protected getReportConfig(): ReportConfig {
    return MANUSCRIPT_REVIEW_REPORT_CONFIG;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Typed accessors
  // ─────────────────────────────────────────────────────────────────────────

  getManuscriptName(): string {
    return this.reportData?.manuscript_name ?? '';
  }

  getReviewerName(): string {
    return this.reportData?.reviewer_name ?? '';
  }

  getReviewedAt(): Date | null {
    const raw = this.reportData?.reviewed_at;
    return raw ? new Date(raw) : null;
  }

  getAggregateMetrics(): ManuscriptAggregateMetrics {
    return {
      overall_score: this.reportData?.overall_score ?? null,
      pacing_score: this.reportData?.pacing_score ?? null,
      engagement_score: this.reportData?.engagement_score ?? null,
      clarity_score: this.reportData?.clarity_score ?? null,
      prose_quality_score: this.reportData?.prose_quality_score ?? null,
      dialogue_score: this.reportData?.dialogue_score ?? null,
      emotional_impact_score: this.reportData?.emotional_impact_score ?? null,
    };
  }

  getDominantTone(): string | null {
    return this.reportData?.dominant_tone ?? null;
  }

  getFlagFrequency(): Record<string, number> {
    return this.reportData?.flag_frequency ?? {};
  }

  getTagFrequency(): Record<string, number> {
    return this.reportData?.tag_frequency ?? {};
  }

  getOverallAssessment(): string | null {
    return this.reportData?.overall_assessment ?? null;
  }

  getStrengths(): string[] {
    return this.reportData?.strengths ?? [];
  }

  getWeaknesses(): string[] {
    return this.reportData?.weaknesses ?? [];
  }

  getRecommendedChanges(): string[] {
    return this.reportData?.recommended_changes ?? [];
  }

  getReviewStats(): { totalParagraphs: number; paragraphsReviewed: number; wordCount: number; completionPercent: number } {
    const total = this.reportData?.total_paragraphs ?? 0;
    const reviewed = this.reportData?.paragraphs_reviewed ?? 0;
    return {
      totalParagraphs: total,
      paragraphsReviewed: reviewed,
      wordCount: this.reportData?.word_count ?? 0,
      completionPercent: total > 0 ? Math.round((reviewed / total) * 100) : 0,
    };
  }

  getParagraphReviews(): ParagraphReview[] {
    return (this.reportData?.paragraph_reviews as ParagraphReview[]) ?? [];
  }

  getFlaggedParagraphs(): ParagraphReview[] {
    return this.getParagraphReviews().filter(p => p.flags.length > 0);
  }

  getLowScoringParagraphs(threshold = 2.5): ParagraphReview[] {
    return this.getParagraphReviews().filter(p => {
      const scores = [
        p.pacing, p.engagement, p.clarity,
        p.prose_quality, p.dialogue_quality, p.emotional_impact,
      ].filter(s => s !== null) as number[];
      if (scores.length === 0) return false;
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      return avg <= threshold;
    });
  }

  // -------------------------------------------------------------------------
  // Derived accessors — computed from paragraph reviews when backend aggregate
  // fields are absent (synthesis step hasn't run yet or didn't write back)
  // -------------------------------------------------------------------------

  /**
   * Flag frequency derived from paragraph reviews when the backend aggregate
   * field is absent. This ensures the MetricsPanel always has data once
   * paragraphs are loaded.
   */
  getEffectiveFlagFrequency(): Record<string, number> {
    const stored = this.reportData?.flag_frequency;
    if (stored && Object.keys(stored).length > 0) return stored;
    const freq: Record<string, number> = {};
    for (const p of this.getParagraphReviews()) {
      for (const flag of p.flags) {
        freq[flag] = (freq[flag] ?? 0) + 1;
      }
    }
    return freq;
  }

  /**
   * Aggregate scores from the backend if any dimension is populated; otherwise
   * computes dimension averages from paragraph-level scores (0–5 → 1–10 scale).
   */
  getEffectiveScores(): ManuscriptAggregateMetrics {
    const stored = this.getAggregateMetrics();
    if (Object.values(stored).some(v => v !== null)) return stored;

    const paragraphs = this.getParagraphReviews();
    if (paragraphs.length === 0) return stored;

    const avgDimension = (key: ParagraphScoreKey): number | null => {
      const vals = paragraphs
        .map(p => p[key] as number | null)
        .filter((v): v is number => v !== null);
      if (vals.length === 0) return null;
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      return Math.round((avg / 5) * 100) / 10;
    };

    const pacing     = avgDimension('pacing');
    const engagement = avgDimension('engagement');
    const clarity    = avgDimension('clarity');
    const prose      = avgDimension('prose_quality');
    const dialogue   = avgDimension('dialogue_quality');
    const impact     = avgDimension('emotional_impact');

    const dimensions = [pacing, engagement, clarity, prose, dialogue, impact]
      .filter((v): v is number => v !== null);
    const overall = dimensions.length > 0
      ? Math.round(dimensions.reduce((a, b) => a + b, 0) / dimensions.length * 10) / 10
      : null;

    return {
      overall_score:         overall,
      pacing_score:          pacing,
      engagement_score:      engagement,
      clarity_score:         clarity,
      prose_quality_score:   prose,
      dialogue_score:        dialogue,
      emotional_impact_score: impact,
    };
  }

  /**
   * Dominant tone from the backend if set; otherwise the most frequent
   * emotional_tone across paragraph reviews.
   */
  getEffectiveDominantTone(): string | null {
    const stored = this.reportData?.dominant_tone;
    if (stored) return stored;
    const freq: Record<string, number> = {};
    for (const p of this.getParagraphReviews()) {
      if (p.emotional_tone) {
        freq[p.emotional_tone] = (freq[p.emotional_tone] ?? 0) + 1;
      }
    }
    const entries = Object.entries(freq);
    return entries.length > 0
      ? entries.sort(([, a], [, b]) => b - a)[0][0]
      : null;
  }

  // -------------------------------------------------------------------------
  // AI / entity_read integration
  // -------------------------------------------------------------------------

  /**
   * Serialize the report into AI-readable nodes:
   *   - One "report_summary" node with all aggregate data
   *   - One "paragraph_review" node per reviewed paragraph
   */
  protected serializeReportNodes(): any[] {
    const nodes: any[] = [];
    const d = this.reportData!;

    // ── Summary node ────────────────────────────────────────────────────────
    const scores = this.getAggregateMetrics();
    const stats = this.getReviewStats();

    const scoreLines = Object.entries(scores)
      .filter(([, v]) => v !== null)
      .map(([k, v]) => `  ${k.replace(/_score$/, '').replace(/_/g, ' ')}: ${v}/10`)
      .join('\n');

    const summaryText = [
      `Manuscript: ${d.manuscript_name}`,
      `Reviewer: ${d.reviewer_name}`,
      `State: ${d.state}`,
      `Reviewed at: ${d.reviewed_at ?? 'n/a'}`,
      `Word count: ${stats.wordCount}`,
      `Paragraphs reviewed: ${stats.paragraphsReviewed}/${stats.totalParagraphs} (${stats.completionPercent}%)`,
      scoreLines ? `\nAggregate scores (1–10):\n${scoreLines}` : '',
      d.overall_assessment ? `\nOverall assessment:\n${d.overall_assessment}` : '',
      d.strengths?.length ? `\nStrengths:\n${d.strengths.map(s => `  - ${s}`).join('\n')}` : '',
      d.weaknesses?.length ? `\nWeaknesses:\n${d.weaknesses.map(w => `  - ${w}`).join('\n')}` : '',
      d.recommended_changes?.length
        ? `\nRecommended changes:\n${d.recommended_changes.map(r => `  - ${r}`).join('\n')}`
        : '',
    ]
      .filter(Boolean)
      .join('\n');

    nodes.push({
      id: 'report_summary',
      className: 'report_summary',
      text: summaryText,
      attrs: {
        state: d.state,
        manuscript_doc_id: d.manuscript_doc_id,
        manuscript_name: d.manuscript_name,
        reviewer_id: d.reviewer_id,
        reviewer_name: d.reviewer_name,
        reviewed_at: d.reviewed_at,
        scores,
        overall_assessment: d.overall_assessment,
        strengths: d.strengths,
        weaknesses: d.weaknesses,
        recommended_changes: d.recommended_changes,
        stats,
      },
    });

    // ── Paragraph review nodes ───────────────────────────────────────────────
    for (const p of this.getParagraphReviews()) {
      const scoreParts: string[] = [];
      if (p.pacing != null) scoreParts.push(`pacing:${p.pacing}`);
      if (p.engagement != null) scoreParts.push(`engagement:${p.engagement}`);
      if (p.clarity != null) scoreParts.push(`clarity:${p.clarity}`);
      if (p.prose_quality != null) scoreParts.push(`prose:${p.prose_quality}`);
      if (p.dialogue_quality != null) scoreParts.push(`dialogue:${p.dialogue_quality}`);
      if (p.emotional_impact != null) scoreParts.push(`emotional_impact:${p.emotional_impact}`);

      const paragraphText = [
        `[P${p.paragraph_index}] ${p.text_preview}`,
        scoreParts.length ? `Scores: ${scoreParts.join(', ')}` : '',
        p.flags.length ? `Flags: ${p.flags.join(', ')}` : '',
        p.emotional_tone ? `Tone: ${p.emotional_tone}` : '',
        p.category_tags.length ? `Tags: ${p.category_tags.join(', ')}` : '',
        p.comment ? `Comment: ${p.comment}` : '',
      ]
        .filter(Boolean)
        .join(' | ');

      nodes.push({
        id: `paragraph_${p.paragraph_index}`,
        className: 'paragraph_review',
        text: paragraphText,
        attrs: p,
      });
    }

    return nodes;
  }
}
